import { applyRoundEvent } from '@cs-shot/battle-sim';
import type { FighterInit, RoundEvent } from '@cs-shot/battle-sim';
import type { Scene } from './types';
import { createMix } from './battleConfig';
import { createPlaybackState, syncTo, type BattleEngineOpts } from './playbackState';
import { createActors } from './actors';
import { scheduleRound } from './schedule';
import { advanceFighters } from './motion';
import { runDueEvents, cullEffects } from './pump';
import { computeField, renderScene } from './compose';
import type { RoundUpdate } from '../game/useRushMidMatch';

export { ROUND_ANIM } from './battleConfig';
export type { BattleEngineOpts };

export interface BattleEngine {
  setUpdate: (u: RoundUpdate, inits: FighterInit[], n: number) => void;
  step: (n: number, cw: number, ch: number, dpr: number, safeTop: number, safeBottom?: number) => void;
  getReplayDur: () => number;
  getActiveAt: () => number;
  getActiveRound: () => number;
}

/**
 * The battle replay engine. Owns the playback state and wires the pipeline: a new round is
 * compiled into a timeline (schedule), whose events mutate the scene as they fire (actors);
 * each frame pumps due events, advances motion, culls effects and paints (pump/motion/compose).
 * Everything is read through opts + refs so a same-round poll never resets it.
 */
export const createBattleEngine = (sc: Scene, opts: BattleEngineOpts): BattleEngine => {
  const state = createPlaybackState();
  const mix = createMix(opts.getYouId);

  // Commit one event to the replay cursor (battle-sim's applyRoundEvent — the SAME code
  // the history fold runs) and mirror the result onto the scene fighters. This is the ONLY
  // place hp/armor/kit change mid-replay: the actors draw effects, the sim's own accounting
  // decides the numbers, so the renderer can't drift from the server again.
  const commit = (ev: RoundEvent) => {
    const cur = state.cursor;
    if (!cur) return;
    applyRoundEvent(cur, ev);
    for (const f of sc.fighters) {
      const c = cur[f.id];
      if (!c) continue;
      f.hp = c.hp;
      f.armor = c.armor;
      f.hasGrenade = c.hasGrenade;
      f.hasSmoke = c.hasSmoke;
      f.hasMedkit = c.hasMedkit;
      f.hasFlash = c.hasFlash;
    }
  };
  const actors = createActors(sc, opts, state, mix, commit);

  const setUpdate = (u: RoundUpdate, nextInits: FighterInit[], n: number) => {
    state.inits = nextInits;
    if (u.round !== state.activeRound && state.inits.length) {
      state.activeRound = u.round;
      scheduleRound(sc, state, actors, u, n);
    }
  };

  const step = (n: number, cw: number, ch: number, dpr: number, safeTop: number, safeBottom = 0) => {
    runDueEvents(sc, state, n, mix, actors, opts.getYouId());
    // reconcile to the authoritative end state once the replay window passes
    if (state.prevFinal && n - state.activeAt > state.replayDur) syncTo(sc, state.prevFinal);
    // still inside THIS round's replay? drives whose enemy the barrels + target ring track
    const replaying = n - state.activeAt < state.replayDur;
    advanceFighters(sc, opts, n, replaying, state);
    cullEffects(sc, n);
    const field = computeField(cw, ch, safeBottom, sc.W, sc.H);
    renderScene(sc, opts, n, cw, ch, dpr, safeTop, field, replaying, state);
  };

  return {
    setUpdate,
    step,
    getReplayDur: () => state.replayDur,
    getActiveAt: () => state.activeAt,
    getActiveRound: () => state.activeRound,
  };
};
